// client/src/components/SharedImageTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

// Image serving base (nginx alias). Default to /rollcall-images
const IMAGE_BASE = (process.env.REACT_APP_IMAGE_BASE_URL || "/rollcall-images").replace(/\/$/, "");

// Admin API base. IMPORTANT: this should usually be "/api"
const API_BASE = (process.env.REACT_APP_ADMIN_API_BASE_URL || "/api").replace(/\/$/, "");

function getImageUrl(img) {
  if (img?.url) return img.url;
  if (img?.relative_path) return `${IMAGE_BASE}/${String(img.relative_path).replace(/^\//, "")}`;
  return "";
}

function getImageLabel(img) {
  return img?.original_name || img?.relative_path || img?.id || "image";
}

function getToken() {
  return localStorage.getItem("token");
}

async function fetchShared(roomName) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/images/share?roomName=${encodeURIComponent(roomName)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // If auth expires, keep UI calm and return null
  if (res.status === 401 || res.status === 403) return null;

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Failed to load shared image");
  return data.shared ?? null;
}

const SharedImageTable = ({ socket, roomName }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  // Current shared image for the ROOM (source of truth via GET /images/share)
  const [shared, setShared] = useState(null);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const fileInputRef = useRef(null);

  // guards against request storms
  const sharedFetchInFlightRef = useRef(false);

  const sharedImg = useMemo(() => shared?.image || null, [shared]);

  const [campaigns, setCampaigns] = useState([]); // [{id, name}]
  const [selectedCampaignId, setSelectedCampaignId] = useState("unsorted"); // "unsorted" | number
  const [newCampaignName, setNewCampaignName] = useState("");

  const filteredImages = useMemo(() => {
    if (selectedCampaignId === "unsorted") {
      return images.filter((img) => img.campaign_id == null);
    }
    return images.filter((img) => Number(img.campaign_id) === Number(selectedCampaignId));
  }, [images, selectedCampaignId]);

  async function loadCampaigns() {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/campaigns`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load campaigns");

      const list = Array.isArray(data?.campaigns) ? data.campaigns : [];
      setCampaigns(list);
    } catch (err) {
      console.warn("Campaign load error:", err?.message || err);
      // keep UI calm: don't hard-fail the whole table if campaigns fail
    }
  }

  async function loadImages() {
    try {
      setError("");

      const token = getToken();
      const res = await fetch(`${API_BASE}/images`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to fetch image list");

      const list = Array.isArray(data?.images) ? data.images : [];
      setImages(list);

      // Keep selection if still present, otherwise pick first in (unfiltered) list for now.
      setSelectedImage((prev) => {
        if (prev && list.some((x) => x.id === prev.id)) return prev;
        return list[0] || null;
      });
    } catch (err) {
      console.error("Image load error:", err);
      setError("Could not load images from server.");
    }
  }

  // Keep selection sensible when filter changes or images list updates
  useEffect(() => {
    setSelectedImage((prev) => {
      if (prev && filteredImages.some((x) => x.id === prev.id)) return prev;
      return filteredImages[0] || null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, images]); // filteredImages is derived from these

  async function refreshShared(force = false) {
    if (!roomName) return;

    if (sharedFetchInFlightRef.current && !force) return;
    sharedFetchInFlightRef.current = true;

    try {
      const s = await fetchShared(roomName);
      setShared(s);
    } catch (err) {
      console.warn("Shared image refresh failed:", err?.message || err);
      // keep UI calm; don't setError for shared refresh failures
    } finally {
      sharedFetchInFlightRef.current = false;
    }
  }

  useEffect(() => {
    loadCampaigns();
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When room changes, re-sync the shared image
  useEffect(() => {
    if (!roomName) return;
    refreshShared(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  // Socket listeners: react to server broadcasts
  useEffect(() => {
    if (!socket) return;

    const onUpdated = () => refreshShared();
    const onCleared = () => setShared(null);
    const onSync = (sharedValue) => setShared(sharedValue ?? null);

    socket.on("shared-image-updated", onUpdated);
    socket.on("shared-image-cleared", onCleared);
    socket.on("shared-image-sync", onSync);

    return () => {
      socket.off("shared-image-updated", onUpdated);
      socket.off("shared-image-cleared", onCleared);
      socket.off("shared-image-sync", onSync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, roomName]);

  async function createCampaign() {
    const name = newCampaignName.trim();
    if (!name) return;

    try {
      setBusy(true);
      setError("");

      const token = getToken();
      if (!token) {
        setError("Missing auth token. Please log in again.");
        return;
      }

      const res = await fetch(`${API_BASE}/campaigns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Create campaign failed");

      // Expect server returns { campaign: {id, name} } or the campaign itself
      const created = data.campaign || data;

      setCampaigns((prev) => [created, ...prev]);
      setSelectedCampaignId(created.id);
      setNewCampaignName("");
    } catch (err) {
      console.error("Create campaign error:", err);
      setError(err.message || "Create campaign failed.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteSelectedCampaign() {
  if (selectedCampaignId === "unsorted") return;

  const campaign = campaigns.find((c) => Number(c.id) === Number(selectedCampaignId));
  const label = campaign?.name || "this campaign";

  const ok = window.confirm(`Delete campaign "${label}" and ALL images in it? This cannot be undone.`);
  if (!ok) return;

  try {
    setBusy(true);
    setError("");

    const token = getToken();
    if (!token) {
      setError("Missing auth token. Please log in again.");
      return;
    }

    const res = await fetch(`${API_BASE}/campaigns/${selectedCampaignId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Delete campaign failed");

    // Remove campaign locally, switch to Unsorted, reload images
    setCampaigns((prev) => prev.filter((c) => Number(c.id) !== Number(selectedCampaignId)));
    setSelectedCampaignId("unsorted");
    await loadImages();
  } catch (err) {
    console.error("Delete campaign error:", err);
    setError(err.message || "Delete campaign failed.");
  } finally {
    setBusy(false);
  }
}

  async function handleUploadSelectedFile(file) {
    if (!file) return;

    try {
      setBusy(true);
      setError("");

      const token = getToken();
      if (!token) {
        setError("Missing auth token. Please log in again.");
        return;
      }

      const fd = new FormData();
      fd.append("image", file);

      if (selectedCampaignId !== "unsorted") {
        fd.append("campaign_id", String(selectedCampaignId));
      }

      const res = await fetch(`${API_BASE}/images/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Upload failed");

      // Optimistically add and select
      setImages((prev) => [data, ...prev]);
      setSelectedImage(data);

      // Also reload list to stay perfectly in sync
      loadImages();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function deleteSelectedImage() {
    if (!selectedImage) return;

    try {
      setBusy(true);
      setError("");

      const token = getToken();
      if (!token) {
        setError("Missing auth token. Please log in again.");
        return;
      }

      const label = selectedImage.original_name || "this image";
      const ok = window.confirm(`Delete "${label}" permanently?`);
      if (!ok) return;

      const res = await fetch(`${API_BASE}/images/${selectedImage.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Delete failed");

      setSelectedImage(null);
      loadImages();
    } catch (err) {
      console.error("Delete error:", err);
      setError(err.message || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  function shareSelectedImage() {
    if (!socket || !roomName) return;
    if (!selectedImage?.id) return;

    setError("");

    // optimistic: show the selected image as "Now Showing" immediately
    setShared((prev) => ({
      ...(prev || {}),
      image: selectedImage,
    }));

    socket.emit("shared-image-set", { room: roomName, imageId: selectedImage.id }, (ack) => {
      if (!ack?.ok) {
        setError(ack?.error || "Failed to share image");
        refreshShared(true);
        return;
      }
      refreshShared(true);
    });
  }

  function clearSharedImage() {
    if (!socket || !roomName) return;

    setError("");
    setShared(null);

    socket.emit("shared-image-clear", { room: roomName }, (ack) => {
      if (!ack?.ok) {
        setError(ack?.error || "Failed to clear shared image");
        refreshShared(true);
        return;
      }
      setShared(null);
    });
  }

  return (
    <div
      style={{
        width: "300px",
        background: "#222",
        borderRight: "2px solid #444",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      {/* Title */}
      <h2
        style={{
          textAlign: "center",
          color: "white",
          fontSize: "1.0rem",
          marginBottom: "0.75rem",
        }}
      >
        Image Table
      </h2>

      {/* Current shared */}
      <div
        style={{
          border: "1px solid #444",
          borderRadius: "8px",
          padding: "8px",
          marginBottom: "10px",
          color: "white",
          background: "#1d1d1d",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "6px" }}>
          <strong style={{ fontSize: "0.9rem" }}>Now Showing</strong>

          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => refreshShared(true)}
              style={{
                padding: "4px 8px",
                background: "transparent",
                border: "1px solid #555",
                color: "#ddd",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.8rem",
              }}
              title="Refresh"
            >
              ↻
            </button>

            <button
              onClick={clearSharedImage}
              style={{
                padding: "4px 8px",
                background: "transparent",
                border: "1px solid #555",
                color: "#ddd",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.8rem",
              }}
              title="Clear shared image"
            >
              Clear
            </button>
          </div>
        </div>

        {!sharedImg && <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Nothing shared yet.</div>}

        {sharedImg && (
          <img
            src={getImageUrl(sharedImg)}
            alt="Shared image"
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              borderRadius: "6px",
              border: "1px solid #333",
            }}
            onError={(e) => console.error("Shared image failed:", e?.target?.src)}
          />
        )}
      </div>

      {/* Library controls (no section title) */}
      <div style={{ color: "white", fontSize: "0.9rem" }}>
        {error && <p style={{ color: "salmon" }}>{error}</p>}

        {/* Campaign selector */}
        <div style={{ marginBottom: "8px" }}>
          <label style={{ fontSize: "0.85em", color: "#ccc" }}>Campaign</label>
          <select
            disabled={busy}
            value={selectedCampaignId}
            onChange={(e) => setSelectedCampaignId(e.target.value)}
            style={{
              width: "100%",
              padding: "4px",
              marginTop: "2px",
              background: "#111",
              color: "#eee",
              border: "1px solid #444",
            }}
          >
            <option value="unsorted">Unsorted</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "4px", marginBottom: "10px" }}>
          <input
            disabled={busy}
            type="text"
            placeholder="New campaign…"
            value={newCampaignName}
            onChange={(e) => setNewCampaignName(e.target.value)}
            style={{
              flex: 1,
              padding: "4px",
              background: "#111",
              color: "#eee",
              border: "1px solid #444",
            }}
          />
          <button onClick={createCampaign} disabled={busy || !newCampaignName.trim()}>
            +
          </button>
        </div>

        <div style={{ marginBottom: "10px", display: "flex", gap: "8px" }}>
          {/* Upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={(e) => handleUploadSelectedFile(e.target.files?.[0])}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            style={{
              flex: 1,
              padding: "8px",
              background: "#3B5A78",
              border: "1px solid #555",
              color: "white",
              cursor: busy ? "not-allowed" : "pointer",
              borderRadius: "6px",
              opacity: busy ? 0.8 : 1,
            }}
          >
            {busy ? "Uploading..." : "Upload"}
          </button>

          {/* Delete selected */}
          <button
            onClick={deleteSelectedImage}
            disabled={!selectedImage || busy}
            style={{
              flex: 1,
              padding: "8px",
              background: !selectedImage ? "#444" : "#8a2f2f",
              border: "1px solid #555",
              color: "white",
              cursor: !selectedImage || busy ? "not-allowed" : "pointer",
              borderRadius: "6px",
              opacity: busy ? 0.8 : 1,
            }}
            title={!selectedImage ? "Select an image to delete" : "Delete the selected image"}
          >
            Delete
          </button>

          {/* Share selected */}
          <button
            onClick={shareSelectedImage}
            disabled={!selectedImage || busy || !roomName}
            style={{
              flex: 1,
              padding: "8px",
              background: !selectedImage || !roomName ? "#444" : "#2a6f4b",
              border: "1px solid #555",
              color: "white",
              cursor: !selectedImage || busy || !roomName ? "not-allowed" : "pointer",
              borderRadius: "6px",
              opacity: busy ? 0.8 : 1,
            }}
            title={!roomName ? "No room selected" : "Share the selected image to the room"}
          >
            Share
          </button>
        </div>

        {filteredImages.length === 0 && !error && <p style={{ opacity: 0.85 }}>No images in this campaign.</p>}

        {filteredImages.length > 0 && (
          <div style={{ maxHeight: "260px", overflowY: "auto" }}>
            {filteredImages.map((img) => {
              const isSelected = selectedImage?.id === img.id;
              const thumbSrc = getImageUrl(img);
              const label = getImageLabel(img);

              return (
                <div
                  key={img.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #444",
                    background: isSelected ? "#333" : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={thumbSrc}
                    alt={label}
                    style={{
                      width: "60px",
                      height: "40px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      marginRight: "8px",
                      border: "1px solid #333",
                    }}
                    onError={(e) => console.error("Library thumb failed:", e?.target?.src)}
                  />
                  <span style={{ wordBreak: "break-word", fontSize: "0.85rem" }}>{label}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Campaign delete (intentionally at bottom) */}
<div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #444" }}>
  <button
    onClick={deleteSelectedCampaign}
    disabled={busy || selectedCampaignId === "unsorted"}
    style={{
      width: "100%",
      padding: "10px",
      background: selectedCampaignId === "unsorted" ? "#333" : "#6b1f1f",
      border: "1px solid #555",
      color: "white",
      cursor: busy || selectedCampaignId === "unsorted" ? "not-allowed" : "pointer",
      borderRadius: "6px",
      opacity: busy ? 0.8 : 1,
      fontWeight: 700,
    }}
    title={
      selectedCampaignId === "unsorted"
        ? "Unsorted cannot be deleted."
        : "Delete this campaign and all images in it"
    }
  >
    Delete Campaign
  </button>

  <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#aaa", lineHeight: 1.3 }}>
    This permanently deletes the selected campaign <span style={{ color: "#ddd" }}>and all images inside it</span>.
  </div>
</div>

      </div>
    </div>
  );
};

export default SharedImageTable;
